import type { StatData } from "../types/stats";
import { normalizeItemName } from "./itemAliases";

// ─── フレーム⇔年変換 ────────────────────────────────

/** メインシーンのデフォルト定数 */
const DEFAULT_START_FRAME = 150;
const DEFAULT_END_FRAME = 750;
const DEFAULT_START_YEAR = 2000;
const DEFAULT_END_YEAR = 2025;

/**
 * Composition の絶対フレームを浮動小数年に変換する。
 * 範囲外は clamp される。
 */
export function frameToYear(
  absoluteFrame: number,
  startFrame = DEFAULT_START_FRAME,
  endFrame = DEFAULT_END_FRAME,
  startYear = DEFAULT_START_YEAR,
  endYear = DEFAULT_END_YEAR,
): number {
  const t = Math.max(0, Math.min(1, (absoluteFrame - startFrame) / (endFrame - startFrame)));
  return startYear + t * (endYear - startYear);
}

// ─── データ取得 ──────────────────────────────────────

/**
 * 整数年・都市・品目の支出金額を返す。
 * 品目名は normalizeItemName でエイリアス解決する。
 * データが存在しない場合は null。
 */
export function getAmount(
  data: StatData,
  year: number,
  city: string,
  item: string,
): number | null {
  const yearData = data.data[year];
  if (!yearData) return null;

  const cityData = yearData[city];
  if (!cityData) return null;

  const normalized = normalizeItemName(item);

  // 正規化名で検索
  if (cityData[normalized]) return cityData[normalized].amount;

  // 元の名前でも検索 (エイリアスマップに無い表記ゆれ対策)
  if (normalized !== item && cityData[item]) return cityData[item].amount;

  return null;
}

/**
 * 小数年をフロア年・シーリング年で線形補間する。
 * どちらかのデータが null なら null を返す。
 */
export function getInterpolatedAmount(
  data: StatData,
  floatYear: number,
  city: string,
  item: string,
): number | null {
  const yearLow = Math.floor(floatYear);
  const yearHigh = Math.ceil(floatYear);

  // 整数年の場合はそのまま返す
  if (yearLow === yearHigh) {
    return getAmount(data, yearLow, city, item);
  }

  const lo = getAmount(data, yearLow, city, item);
  const hi = getAmount(data, yearHigh, city, item);

  if (lo === null || hi === null) return null;

  const t = floatYear - yearLow;
  return lo + t * (hi - lo);
}
